"use client";

import { useState } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PageHero from "@/components/sections/PageHero";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiArrowRight,
  HiX,
  HiCheckCircle,
  HiLocationMarker,
  HiClock,
  HiBriefcase,
} from "react-icons/hi";
import { Button } from "@/components/ui/button";
import { getCareerBenefits, getJobOpenings } from "@/data/career";
import { useLanguage } from "@/lib/i18n";

export default function CareerPage() {
  const { language } = useLanguage();
  const benefits = getCareerBenefits(language);
  const jobOpenings = getJobOpenings(language);
  
  const [selectedJob, setSelectedJob] = useState<typeof jobOpenings[0] | null>(null);

  const pageContent = language === "id" ? {
    title: "Karir & Peluang",
    subtitle: "Bergabunglah dengan tim yang berdedikasi untuk menghadirkan pengalaman luxury co-living terbaik di Bali.",
    superText: "Join Our Team",
    whyJoin: "Why Join Us?",
    growTitle: "Tumbuh Bersama KinKen",
    growSubtitle: "Kami percaya bahwa tim yang bahagia akan menciptakan pengalaman luxury living yang luar biasa bagi setiap penghuni.",
    positionsTitle: "Posisi Tersedia",
    positionsSubtitle: "Temukan peran yang sesuai dengan passion dan keahlian Anda.",
    detailApply: "Detail & Apply",
    jobDescription: "Deskripsi Pekerjaan",
    qualifications: "Kualifikasi",
    close: "Tutup",
    sendApplication: "Kirim Lamaran via Email",
  } : {
    title: "Career & Opportunities",
    subtitle: "Join the team dedicated to delivering the best luxury co-living experience in Bali.",
    superText: "Join Our Team",
    whyJoin: "Why Join Us?",
    growTitle: "Grow With KinKen",
    growSubtitle: "We believe that a happy team will create an extraordinary luxury living experience for every resident.",
    positionsTitle: "Open Positions",
    positionsSubtitle: "Find the role that suits your passion and expertise.",
    detailApply: "Details & Apply",
    jobDescription: "Job Description",
    qualifications: "Qualifications",
    close: "Close",
    sendApplication: "Send Application via Email",
  };

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        title={pageContent.title}
        subtitle={pageContent.subtitle}
        image="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070&auto=format&fit=crop"
        superText={pageContent.superText}
      />

      {/* VALUES & BENEFITS SECTION */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="text-brand-gold text-xs font-bold tracking-[0.25em] uppercase block mb-4">
              {pageContent.whyJoin}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl text-brand-charcoal mb-6">
              {pageContent.growTitle}
            </h2>
            <p className="text-gray-500 font-light text-lg">
              {pageContent.growSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((item, idx) => (
              <div
                key={idx}
                className="p-8 border border-gray-100 rounded-lg hover:border-brand-gold hover:shadow-lg transition-all duration-300 group bg-[#FAFAFA] hover:bg-white"
              >
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center text-brand-gold shadow-sm mb-6 group-hover:bg-brand-gold group-hover:text-white transition-colors">
                  <item.icon size={28} />
                </div>
                <h3 className="font-serif text-xl text-brand-charcoal mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-500 text-sm font-light leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPEN POSITIONS SECTION */}
      <section className="py-20 md:py-32 bg-[#F9F9F9]">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div>
              <h2 className="font-serif text-3xl md:text-5xl text-brand-charcoal mb-4">
                {pageContent.positionsTitle}
              </h2>
              <p className="text-gray-500 font-light">
                {pageContent.positionsSubtitle}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {jobOpenings.map((job) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white p-8 md:p-10 rounded-lg border border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 hover:shadow-xl transition-shadow duration-300"
              >
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-brand-cream text-brand-gold px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                      {job.department}
                    </span>
                    <span className="text-gray-400 text-xs flex items-center gap-1">
                      <HiClock size={12} /> {job.type}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl text-brand-charcoal mb-2">
                    {job.role}
                  </h3>
                  <div className="flex items-center gap-2 text-gray-500 text-sm font-light">
                    <HiLocationMarker size={14} /> {job.location}
                  </div>
                </div>

                <Button
                  onClick={() => setSelectedJob(job)}
                  className="rounded-none bg-white text-brand-charcoal border border-brand-charcoal hover:bg-brand-charcoal hover:text-white px-8 py-6 uppercase text-xs font-bold tracking-[0.2em] transition-all min-w-[160px]"
                >
                  {pageContent.detailApply}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* JOB DETAIL MODAL */}
      <AnimatePresence>
        {selectedJob && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex justify-end"
            onClick={() => setSelectedJob(null)}
          >
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="w-full md:w-[600px] bg-white h-full shadow-2xl overflow-y-auto relative flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8 md:p-10 border-b border-gray-100 sticky top-0 bg-white z-10 flex justify-between items-start">
                <div>
                  <span className="text-brand-gold text-xs font-bold uppercase tracking-wider block mb-2">
                    {selectedJob.department}
                  </span>
                  <h3 className="font-serif text-3xl text-brand-charcoal">
                    {selectedJob.role}
                  </h3>
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <HiBriefcase size={14} /> {selectedJob.type}
                    </span>
                    <span className="flex items-center gap-1">
                      <HiLocationMarker size={14} /> {selectedJob.location}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <HiX size={24} className="text-gray-400" />
                </button>
              </div>

              <div className="p-8 md:p-10 space-y-8 flex-grow">
                <div>
                  <h4 className="font-bold text-lg text-brand-charcoal mb-4">
                    {pageContent.jobDescription}
                  </h4>
                  <p className="text-gray-500 font-light leading-relaxed">
                    {selectedJob.description}
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-lg text-brand-charcoal mb-4">
                    {pageContent.qualifications}
                  </h4>
                  <ul className="space-y-3">
                    {selectedJob.requirements.map((item, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3 text-gray-500 font-light text-sm"
                      >
                        <HiCheckCircle
                          size={16}
                          className="mt-1 shrink-0 text-brand-gold"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="p-6 border-t border-gray-100 bg-gray-50 sticky bottom-0 flex justify-end gap-4">
                <Button
                  variant="outline"
                  onClick={() => setSelectedJob(null)}
                  className="rounded-none border-gray-300 hover:bg-white"
                >
                  {pageContent.close}
                </Button>
                <Button
                  className="rounded-none bg-brand-charcoal hover:bg-brand-gold text-white px-8 shadow-lg"
                  onClick={() =>
                    (window.location.href = `mailto:career@kinken-kost.com?subject=${language === "id" ? "Lamaran" : "Application"}: ${selectedJob.role}`)
                  }
                >
                  {pageContent.sendApplication}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </main>
  );
}
